import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Box, List, ListItemButton, ListItemText, Paper, Typography } from '@mui/material';
import { ReactRenderer } from '@tiptap/react';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import { formatUserDisplayName } from 'components/utils';
import { User } from 'components/types';
import { sendApiRequest } from 'lib/api/public';

export type MentionUserItem = {
  id: string;
  label: string;
};

type MentionListHandle = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

type MentionListProps = SuggestionProps<MentionUserItem>;

let usersCache: User[] | null = null;
let usersPromise: Promise<User[]> | null = null;

const fetchMentionableUsers = async (): Promise<User[]> => {
  if (usersCache) {
    return usersCache;
  }

  if (!usersPromise) {
    usersPromise = sendApiRequest<{ list: User[] }>({ path: '/users/mentionable', method: 'GET' })
      .then(response => {
        usersCache = response.list || [];
        return usersCache;
      })
      .catch(() => {
        usersPromise = null;
        return [] as User[];
      });
  }

  return usersPromise;
};

const positionElement = (element: HTMLElement, clientRect?: (() => DOMRect | null) | null) => {
  const rect = clientRect?.();

  if (!rect) {
    return;
  }

  element.style.position = 'absolute';
  element.style.left = `${window.scrollX + rect.left}px`;
  element.style.top = `${window.scrollY + rect.bottom + 4}px`;
  element.style.zIndex = '1400';
};

const MentionList = forwardRef<MentionListHandle, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];

    if (item) {
      props.command(item);
    }
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex(prev => (prev + props.items.length - 1) % Math.max(props.items.length, 1));
        return true;
      }

      if (event.key === 'ArrowDown') {
        setSelectedIndex(prev => (prev + 1) % Math.max(props.items.length, 1));
        return true;
      }

      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }

      return false;
    },
  }));

  return (
    <Paper elevation={8} sx={{ minWidth: 220, maxWidth: 320, maxHeight: 240, overflow: 'auto' }}>
      {props.items.length === 0 ? (
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No team members found
          </Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {props.items.map((item, index) => (
            <ListItemButton
              key={item.id}
              selected={index === selectedIndex}
              onClick={() => selectItem(index)}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
});

MentionList.displayName = 'MentionList';

const mentionSuggestion: Omit<SuggestionOptions<MentionUserItem>, 'editor'> = {
  char: '@',
  allowSpaces: false,
  items: async ({ query }) => {
    const users = await fetchMentionableUsers();
    const normalizedQuery = query.toLowerCase();

    return users
      .map(user => ({
        id: user._id,
        label: formatUserDisplayName(user) || user.email || user._id,
      }))
      .filter(item => {
        if (!normalizedQuery) {
          return true;
        }

        return item.label.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 10);
  },
  render: () => {
    let component: ReactRenderer<MentionListHandle, MentionListProps> | null = null;

    return {
      onStart: props => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        document.body.appendChild(component.element);
        positionElement(component.element, props.clientRect);
      },

      onUpdate: props => {
        component?.updateProps(props);

        if (!props.clientRect || !component) {
          return;
        }

        positionElement(component.element, props.clientRect);
      },

      onKeyDown: props => {
        if (props.event.key === 'Escape') {
          return true;
        }

        return component?.ref?.onKeyDown(props) ?? false;
      },

      onExit: () => {
        component?.element.remove();
        component?.destroy();
        component = null;
      },
    };
  },
};

export default mentionSuggestion;
